import {
  Collision,
  CollisionDetection,
  pointerWithin,
  rectIntersection
} from '@dnd-kit/core';
import { isOpeningDragType, OPENING_SLIDE_MARGIN_PX } from 'utils/opening-drag';

type RectType = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export function isOpeningDragActiveType(type: unknown): boolean {
  return (
    type === 'grid-window' ||
    type === 'grid-door' ||
    (typeof type === 'string' && isOpeningDragType(type))
  );
}

export function isOpeningDropCollisionId(id: unknown): boolean {
  return String(id).startsWith('drop-') || id === 'item-list-container';
}

export function preferOpeningDropCollisions<T extends { id: string | number }>(
  collisions: T[]
): T[] {
  const preferred = collisions.filter((collision) =>
    isOpeningDropCollisionId(collision.id)
  );
  if (preferred.length > 0) {
    return preferred;
  }
  return collisions;
}

export function isPointerNearRect(
  pointer: { x: number; y: number } | null,
  rect: RectType | null | undefined,
  marginPx: number
): boolean {
  if (!pointer || !rect) {
    return false;
  }
  return (
    pointer.x >= rect.left - marginPx &&
    pointer.x <= rect.left + rect.width + marginPx &&
    pointer.y >= rect.top - marginPx &&
    pointer.y <= rect.top + rect.height + marginPx
  );
}

export function homeSpaceDroppableId(spaceId: unknown): string | null {
  if (typeof spaceId !== 'string' || spaceId.length === 0) {
    return null;
  }
  return `drop-${spaceId}`;
}

function collisionForDroppable(
  args: Parameters<CollisionDetection>[0],
  id: string
): Collision | null {
  const droppableContainer = args.droppableContainers.find(
    (container) => container.id === id
  );
  if (!droppableContainer || droppableContainer.disabled) {
    return null;
  }
  return {
    id,
    data: {
      droppableContainer,
      value: 1
    }
  };
}

export const openingAwareCollisionDetection: CollisionDetection = (args) => {
  const collisions = rectIntersection(args);
  if (!isOpeningDragActiveType(args.active.data.current?.type)) {
    return collisions;
  }

  const spaceOrListHits = collisions.filter((collision) =>
    isOpeningDropCollisionId(collision.id)
  );
  if (spaceOrListHits.length > 0) {
    return spaceOrListHits;
  }

  const homeId = homeSpaceDroppableId(args.active.data.current?.spaceId);
  if (
    homeId &&
    isPointerNearRect(
      args.pointerCoordinates,
      args.droppableRects.get(homeId),
      OPENING_SLIDE_MARGIN_PX
    )
  ) {
    const homeCollision = collisionForDroppable(args, homeId);
    if (homeCollision) {
      return [homeCollision];
    }
  }

  const pointerHits = pointerWithin(args).filter((collision) =>
    isOpeningDropCollisionId(collision.id)
  );
  if (pointerHits.length > 0) {
    return pointerHits;
  }

  return collisions;
};
